import { Button, Form, Pending } from '@cluey/cluey-components';
import { useMutation } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import isError from 'lodash/isError';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router';
import { z } from 'zod';

import { PATHS, decodeToken, tokenPassword, useSignupStore } from '.';
import { userLogin } from '../../../../actions/hub/hubAuthPagesActions';
import { InitialState } from '../../../../reducers/initialState';
import { PATH_HUB_RESET_PASSWORD_REQUEST } from '../../../../util/pagePath';
import { PasswordField } from '../ResetPasswordRequestPage/ResetPassword/PasswordField';
import RightColumn from '../RightColumn';
import TwoColumnLayout from '../TwoColumnLayout';
import { BackButton, InlineLoader } from '../common';
import useNavigateWithSearch from '../useNavigateWithSearch';
import Stepper from './Stepper';

const PasswordValidation = z
  .string()
  .min(8, 'Password must be at least 8 characters long.')
  .refine((value) => !/[^A-Za-z\d@$!%*?&#^]+/.test(value), 'Password can only contain special characters @$!%*?&#^')
  .refine(
    (value) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&#^]{8,}$/.test(value),
    'Password must contain at least 1 uppercase letter, 1 lowercase letter and 1 number.'
  );

export const SignUpPasswordPage = () => {
  const [password, setPassword] = useState('');
  const navigateWithSearch = useNavigateWithSearch();
  const { email } = useSignupStore(({ origin, email }) => ({ origin, email }));
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();

  const signingIn = useSelector((state: InitialState) => state.ui.apiState.hubAuthPage.isLoading);
  const {
    mutate,
    isLoading,
    error: authError,
  } = useMutation({
    mutationFn: async (newPassword: string) => {
      try {
        // grab the current user from Auth
        const user = await Auth.currentAuthenticatedUser();
        // Decode the token stored in the store
        const token = decodeToken(useSignupStore.getState().token);
        // Execute an Auth password change
        await Auth.changePassword(user, tokenPassword(token), newPassword);
        useSignupStore.setState({ token: '' });
      } catch (error) {
        if (error === 'The user is not authenticated') {
          // Must have arrived via the legacy signup method
          const { email, tempPwd, clearState } = useSignupStore.getState();
          if (!tempPwd) {
            // edge case
            navigateWithSearch(PATH_HUB_RESET_PASSWORD_REQUEST, { emailParam: email });
          }
          // sign them in, then change their password straight away
          const user = await Auth.signIn(email, tempPwd);
          await Auth.completeNewPassword(user, newPassword);
          // Dispatch redux useLogin action
          await userLogin({ email, password: newPassword })(dispatch);
          // clear the state -- not needed anymore
          clearState();
        } else {
          throw error;
        }
      }
    },
    onSuccess: async () => {
      /* 
        OUTDATED:
        This seems counterintuitive, but is needed flow to prevent skipping of the contact page.
        It's because the userLogin action modifies the "authenticated" redux state, which is
        checked by a useEffect hook onMount in /views/hub/auth2/common.ts.

        On rendering the contact page, the authenticated state must first be unauthenticated to prevent
        forgoing the update phone number page.

        To achieve this, redirection must precede the userLogin action.
      */
      await userLogin({ email, password })(dispatch);

      /*
      Instead of redirecting to CONTACT_PATH, it should now redirect to a new URL
      SIGNUP_STUDENT_DETAILS_PATH - provide student details
      SIGNUP_REVIEW_DETAILS_PATH - review and confirm the provided details
      SIGNUP_SUCCESS_PATH - success page/confirmation page
      */
      navigateWithSearch(PATHS.STUDENT_DETAILS_PATH);
    },
  });

  const handleSubmit = () => {
    const parseResult = PasswordValidation.safeParse(password);
    if (parseResult.success === true) {
      mutate(parseResult.data);
    } else {
      setError(parseResult.error.errors[0].message);
    }
  };

  if (!email) return <Redirect to={`${PATHS.ROOT_PATH}${location.search}`} />;

  const renderLeftColumn = () => (
    <div className="ml-auto mr-auto box-content flex h-full max-w-[420px] flex-col overflow-y-auto px-5">
      <div className="mt-10">
        <BackButton />
      </div>
      <div className="mb-6 mt-6">
        <Stepper steps={5} activeStep={3} />
      </div>
      <h2 className="mb-4 font-display text-4xl font-bold">Create your password</h2>
      <Pending
        loading={signingIn}
        loader={
          <div className="flex h-full w-full items-center justify-center">
            <InlineLoader />
          </div>
        }
      >
        <p className="pb-6">Create a strong password for Cluey</p>
        <p className="pb-6">Must include at least:</p>
        <ul className="list-disc pl-8">
          <li>8 characters </li>
          <li>1 uppercase letter </li>
          <li>1 lowercase letter </li>
          <li>1 number</li>
        </ul>
        <Form onSubmit={handleSubmit} className="w-full py-8">
          <PasswordField
            id="newPassword"
            label="Password"
            onChange={(value) => {
              setPassword(value);
              setError('');
            }}
            data={{
              error:
                error ||
                (isError(authError) ? (authError as Error).message : typeof authError === 'string' && authError) ||
                '',
              valid: error === '',
              value: password,
              textVisible: showPassword,
            }}
            onEyeClick={() => setShowPassword((curr) => !curr)}
            className="max-w-sm pb-16"
          />
          <Button type="submit" size="lg" disabled={isLoading} className="relative flex items-center justify-center">
            <span className={isLoading ? 'opacity-0' : ''}>Continue</span>
            {isLoading && <InlineLoader className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />}
          </Button>
        </Form>
      </Pending>
    </div>
  );

  const renderRightColumn = () => <RightColumn step={2} />;

  return <TwoColumnLayout renderLeftColumn={renderLeftColumn} renderRightColumn={renderRightColumn} />;
};
